import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { getGlobalScope, getProjectScope, getReleaseGroupScope } from '../../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { Scope } from '../../../../../shared/issuesV2';
import type { IssuesContainerParams } from '../../IssuesContainer';

type UseSetIssuesScopeParams =
  Omit<IssuesContainerParams, 'category' | 'reference' | 'reference_type'>
  & Partial<{ revisionScanId: number, releaseScanId: number}>;

/**
 * Returns the scope based on the URL params found.
 * Looks for project scope first, then release group scope,
 * before finally falling back to global scope.
 * Note that projectReleaseId comes from ReleaseGroupIssuesContainer
 * and not from the URL itself.
 */
const getScope = ({
  project_id: projectId,
  revision_id: revisionId,
  revisionScanId,
  releaseScanId,
  projectGroupId,
  projectReleaseId,
}: UseSetIssuesScopeParams): Scope => {
  if (projectId) {
    return getProjectScope(projectId, revisionId, revisionScanId);
  }
  if (projectGroupId) {
    return getReleaseGroupScope(projectGroupId, projectReleaseId as number, releaseScanId);
  }
  return getGlobalScope();
};

/**
 * Hook which reads the projectId and revisionId from the URL params (if present),
 * and sets the scope accordingly. This runs on mount, and again whenever any of the
 * scope-defining params change (eg: the user selects a different release/scan without
 * the component remounting).
 *
 * @param {object} params - params object
 * @param {string} params.projectId - A given project ID, from the URL params.
 * @param {string} params.revisionId - A given revision ID, from the URL params.
 * @param {string} params.projectGroupId - A given release group ID, from the URL params.
 * @param {string} params.projectReleaseId - A given release ID, from the URL params.
 */
const useSetIssuesScope = (params: UseSetIssuesScopeParams): Scope => {
  const dispatch = useDispatch();
  const {
    project_id: projectId,
    revision_id: revisionId,
    revisionScanId,
    releaseScanId,
    projectGroupId,
    projectReleaseId,
  } = params;

  //  Memoized on the primitive params (rather than `params` itself, whose object identity is
  //  unstable across renders) so the effect below only re-fires when one of them actually changes.
  const scope = useMemo(
    () => getScope({
      project_id: projectId,
      revision_id: revisionId,
      revisionScanId,
      releaseScanId,
      projectGroupId,
      projectReleaseId,
    }),
    [projectId, revisionId, revisionScanId, releaseScanId, projectGroupId, projectReleaseId],
  );

  useEffect(() => {
    dispatch(IssuesV2Store.actions.setScope(scope));
  }, [dispatch, scope]);

  //  We return scope here so that it can be passed directly to `useIssuesSubnav`. See IssuesContainer for more info.
  return scope;
};

export default useSetIssuesScope;
